import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { Channel, DeleteChatScope } from '@orbit-sync/shared-types';
import { useChatStore } from '../chat/chatStore.js';
import { api, errorMessage } from '../lib/api/index.js';
import { useSettingsStore } from '../settings/settingsStore.js';
import { ConfirmDialog } from './ConfirmDialog.js';

/**
 * "Delete chat" for a direct conversation, "Leave group" for a group, "Clear"
 * for Saved messages.
 *
 * A direct chat can go for this user only - it comes back if the other person
 * writes again, and once both have deleted it the server deletes it for real -
 * or, when the administrator allows it, for BOTH people at once, history and
 * all. Leaving a group takes it out of the list; any member can add the person
 * back. Saved messages is emptied but stays.
 */
export function DeleteChatDialog({
  channel,
  title,
  onClose,
}: {
  channel: Channel;
  /** What the user calls this conversation. */
  title: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const removeChannel = useChatStore((state) => state.removeChannel);
  const clearHistory = useChatStore((state) => state.clearHistory);
  const activeChannelId = useChatStore((state) => state.activeChannelId);
  const allowForEveryone = useSettingsStore((state) => state.settings.allowDeleteChatForEveryone);
  const [scope, setScope] = useState<DeleteChatScope>('me');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      await api.channels.deleteChat(channel.id, scope);
      onClose();

      if (channel.type === 'saved') {
        clearHistory(channel.id);
        return;
      }

      if (activeChannelId === channel.id) {
        await navigate('/', { replace: true });
      }

      // After leaving the page, so it never renders "conversation not found".
      removeChannel(channel.id);
    } catch (cause) {
      setError(errorMessage(cause));
      setBusy(false);
    }
  }

  if (channel.type === 'saved') {
    return (
      <ConfirmDialog
        title="Clear saved messages?"
        confirmLabel="Clear"
        busy={busy}
        error={error}
        onConfirm={() => void confirm()}
        onCancel={onClose}
      >
        <p>Everything in Saved messages is deleted, on all your devices.</p>
      </ConfirmDialog>
    );
  }

  if (channel.type === 'group') {
    return (
      <ConfirmDialog
        title="Leave group?"
        confirmLabel="Leave group"
        busy={busy}
        error={error}
        onConfirm={() => void confirm()}
        onCancel={onClose}
      >
        <p>
          You will leave <strong dir="auto">{title}</strong> and it disappears from your list. Any
          member can add you back later.
        </p>
      </ConfirmDialog>
    );
  }

  return (
    <ConfirmDialog
      title="Delete chat?"
      confirmLabel={scope === 'everyone' ? 'Delete for both' : 'Delete for me'}
      busy={busy}
      error={error}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    >
      <div className="delete-scope" role="radiogroup" aria-label="Delete for">
        <label className="delete-scope-option">
          <input
            type="radio"
            name="delete-scope"
            checked={scope === 'me'}
            onChange={() => setScope('me')}
          />
          <span>
            <strong>Delete for me</strong>
            <span className="field-hint">
              Your conversation with <bdi>{title}</bdi> and its history go for you only. It comes
              back if they message you again.
            </span>
          </span>
        </label>
        {allowForEveryone ? (
          <label className="delete-scope-option">
            <input
              type="radio"
              name="delete-scope"
              checked={scope === 'everyone'}
              onChange={() => setScope('everyone')}
            />
            <span>
              <strong>
                Delete for me and <bdi>{title}</bdi>
              </strong>
              <span className="field-hint">
                The whole conversation, messages and files, is deleted for both of you. This cannot
                be undone.
              </span>
            </span>
          </label>
        ) : null}
      </div>
    </ConfirmDialog>
  );
}
